import React, { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SuccessForm } from '../components/SuccessFrom';
import { Stepper } from '../../../commons/Stepper';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import { authSlice } from '../../../../store/reducers/AuthSlice';
import { ApiError } from '../../../../models/ApiError';
import { getSearchParamValue } from '../../../../utils';
import { authorizationApi } from '../../../../services/apiServices/user/authorization';
import { LocalStorageItems } from '../../../../models/Common';
import { EmailResend } from '../components/EmailResend';
import { useTimer } from '../../../../hooks/useTimer';
import { SetNewPasswordWithoutConfirm } from '../../../forms/SetNewPasswordWithoutConfirm';

interface PasswordRecoveryByEmailProps {
  handleBack: () => void;
  handleComplete: () => void;
}

export const PasswordRecoveryByEmail: FC<PasswordRecoveryByEmailProps> = ({ handleBack, handleComplete }) => {
  const { stepper, authForm } = useAppSelector((state) => state.authReducer);
  const [email, setEmail] = useState('');
  const [submitNewPasswordAPI, { isLoading: isSubmittingNewPasswordLoading }] =
    authorizationApi.useConfirmPasswordRecoveryMutation();
  const [emailResetAPI, { isLoading: isRequestEmailLoading }] = authorizationApi.usePasswordResetMutation();
  const { setFormError, setStep } = authSlice.actions;
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { timer, startTimer, resetTimer } = useTimer(60);

  useEffect(() => {
    startTimer();
  }, []);

  useEffect(() => {
    setEmail(localStorage.getItem(LocalStorageItems.Email) || '');
    setStep(getSearchParamValue('step', search) ? +getSearchParamValue('step', search) || 0 : 0);
  }, []);

  const submitNewPassword = async (password: { password: string; passwordConfirmation: string }) => {
    dispatch(setFormError(null));
    try {
      await submitNewPasswordAPI({
        password: password.password,
        code: getSearchParamValue('code', search),
      })
        .unwrap()
        .then(() => {
          dispatch(setStep(stepper.currentStep + 1));
          localStorage.removeItem(LocalStorageItems.Email);
        });
    } catch (err) {
      const error = err as ApiError;
      dispatch(setFormError(error?.data?.error.message));
    }
  };

  const handleEmailResend = async () => {
    dispatch(setFormError(null));
    try {
      await emailResetAPI({ authId: email })
        .unwrap()
        .then(() => {
          resetTimer();
        });
    } catch (err) {
      const error = err as ApiError;
      dispatch(setFormError(error?.data?.error.message));
    }
  };

  const stepNames = [t('auth.confirmationEmail'), t('auth.newPassword'), t('common.ready')];

  const stepElements = [
    <EmailResend
      timer={timer}
      email={email}
      handleResend={handleEmailResend}
      handleBack={handleBack}
      isLoading={isRequestEmailLoading}
    />,
    <SetNewPasswordWithoutConfirm
      submitNewPassword={submitNewPassword}
      error={authForm.error}
      buttonText={t('buttons.continue')}
      isLoading={isSubmittingNewPasswordLoading}
    />,
    <SuccessForm
      handleClick={handleComplete}
      additionalText={t('auth.yourPasswordHasBeenSuccessfullyChanged')}
      btnLabel={t('buttons.continue')}
    />,
  ];

  return (
    <Stepper
      title={t('auth.createNewPassword')}
      stepNames={stepNames}
      stepElements={stepElements}
      activeStep={stepper.currentStep}
    />
  );
};
